import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { IMAGES } from "@/lib/data";
import { useLang } from "@/i18n";
import { ArrowDownRight, FileDown } from "lucide-react";

const ease = [0.16, 1, 0.3, 1];

const Line = ({ text, i, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span className={`block ${className}`} initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 0.9, ease, delay: 0.2 + i * 0.11 }}>
      {text}
    </motion.span>
  </span>
);

/** Home hero — kinetic masked-line reveal with a subtle parallax backdrop. */
const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.3, 0.7]);
  const { t } = useLang();
  const lines = t("hero.lines");

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen w-full overflow-hidden">
      <motion.div style={{ y, scale }} className="absolute inset-0 -z-10">
        <img src={IMAGES.heroOrchard} alt="Sunlit apple orchard set against rolling Himalayan foothills" className="w-full h-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlay }} className="absolute inset-0 -z-10 bg-[#0d1f12]" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#0d1f12]/70 via-[#0d1f12]/35 to-[#0d1f12]/90" />

      <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12 min-h-screen flex flex-col justify-end pb-16 md:pb-24 pt-32">
        <h1 className="font-display font-semibold tracking-tight leading-[1.02] text-5xl sm:text-7xl lg:text-[6.5vw] max-w-[16ch] text-white">
          {lines.map((l, i) => (
            <Line key={i} text={l} i={i} className={i === 2 ? "italic text-[#e7c46b]" : ""} />
          ))}
        </h1>

        <div className="mt-10 flex flex-col lg:flex-row lg:items-end justify-between gap-8">
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9, duration: 0.8, ease }}
            className="text-base md:text-lg text-white/85 leading-relaxed max-w-xl"
          >
            {t("hero.sub")}
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8, ease }} className="flex flex-wrap gap-3">
            <Link to="/contact" data-testid="hero-cta-quote" className="group inline-flex items-center gap-2 bg-[#d3a53a] text-[#173a1e] font-bold px-8 py-4 rounded-full hover:bg-[#b98a26] transition-colors duration-200">
              {t("common.quote")} <ArrowDownRight size={18} className="group-hover:rotate-45 transition-transform duration-300" />
            </Link>
            <Link to="/contact" data-testid="hero-cta-partner" className="inline-flex items-center gap-2 border border-white/50 text-white font-bold px-8 py-4 rounded-full hover:bg-white hover:text-[#14301b] transition-colors duration-200">
              {t("hero.partner")}
            </Link>
            <a href="/farmsons-company-profile.pdf" target="_blank" rel="noopener noreferrer" data-testid="hero-cta-profile"
              className="inline-flex items-center gap-2 text-white/85 font-bold px-4 py-4 hover:text-[#e7c46b] transition-colors duration-200 underline underline-offset-8 decoration-white/30 hover:decoration-[#e7c46b]">
              <FileDown size={17} /> {t("common.downloadProfile")}
            </a>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
